import { useState, useEffect, useCallback, useRef } from 'react'
import { router, useForm } from '@inertiajs/react'
import { createConsumer } from '@rails/actioncable'
import { useFlashToast } from '../../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft, faCircleDot, faHourglassHalf, faCircleCheck,
  faCircleXmark, faEyeSlash, faLocationDot, faCalendar, faTag,
  faFile, faPaperclip, faBuilding, faGlobe, faUser,
  faPaperPlane, faEye,
  faDownload, faXmark, faExpand, faFilePdf, faFileImage, faFileVideo, faFileAlt,
  faClockRotateLeft, faArrowRightArrowLeft, faFlagCheckered,
  faShieldHalved, faEnvelopeOpen, faComments, faLock, faLockOpen, faBolt, faFileArrowUp, faFileCircleCheck,
  faIdCard, faUserCheck, faFingerprint, faHandshake, faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons'
import AdminSidebar from '../../../components/admin/Sidebar'
import AdminTopbar from '../../../components/admin/Topbar'

const STATUS_CONFIG = {
  menunggu: { label: 'Menunggu',        icon: faCircleDot,     bg: 'bg-gray-100',    text: 'text-gray-600',    ring: 'ring-gray-300',   bar: 'bg-gray-400',    pct: 15 },
  diproses: { label: 'Sedang Diproses', icon: faHourglassHalf, bg: 'bg-amber-50',    text: 'text-amber-700',   ring: 'ring-amber-200',  bar: 'bg-amber-400',   pct: 55 },
  selesai:  { label: 'Selesai',         icon: faCircleCheck,   bg: 'bg-emerald-50',  text: 'text-emerald-700', ring: 'ring-emerald-200', bar: 'bg-emerald-500', pct: 100 },
  ditolak:  { label: 'Ditolak',         icon: faCircleXmark,   bg: 'bg-red-50',      text: 'text-red-600',     ring: 'ring-red-200',    bar: 'bg-red-400',     pct: 100 },
}

const AGREEMENT_TEXT = `PERJANJIAN KERAHASIAAN DAN PERLINDUNGAN IDENTITAS PELAPOR
Sistem Pelaporan Whistleblowing (WBS)

Dengan menyetujui permintaan pembukaan identitas ini, Anda menyatakan telah membaca,
memahami, dan menyetujui seluruh ketentuan berikut:

1. TUJUAN PENGGUNAAN DATA
   Data identitas Anda hanya digunakan oleh admin yang berwenang semata-mata untuk
   keperluan verifikasi, klarifikasi, dan penanganan laporan ini. Tidak ada penggunaan
   data di luar keperluan tersebut yang diizinkan.

2. KERAHASIAAN MUTLAK
   Admin berkomitmen penuh menjaga kerahasiaan identitas Anda. Data tidak akan
   diteruskan, dipublikasikan, atau dibagikan kepada pihak mana pun—termasuk
   terlapor—tanpa persetujuan tertulis dari Anda.

3. LARANGAN PENGUNGKAPAN
   Setiap pengungkapan identitas pelapor yang tidak sah merupakan pelanggaran etika
   serius dan dapat dikenai sanksi disiplin berat serta pertanggungjawaban pidana
   sesuai hukum yang berlaku.

4. DASAR HUKUM PERLINDUNGAN
   Anda berhak atas perlindungan penuh sebagaimana dijamin dalam:
   • UU No. 13 Tahun 2006 tentang Perlindungan Saksi dan Korban
   • PP No. 43 Tahun 2018 tentang Tata Cara Pelaksanaan Peran Serta Masyarakat
   • Peraturan perundang-undangan terkait perlindungan whistleblower yang berlaku

5. PENCATATAN DIGITAL
   Persetujuan ini dicatat secara otomatis dalam sistem dengan cap waktu yang tidak
   dapat diubah, dan berfungsi sebagai bukti perjanjian yang sah antara Anda dan admin.

6. SIFAT KEPUTUSAN
   Pemberian persetujuan ini bersifat sukarela dan berlaku khusus untuk laporan ini
   saja. Setelah disetujui, keputusan tidak dapat dicabut kembali. Jika identitas Anda
   merasa terancam setelah membuka identitas, segera hubungi pengelola sistem.`

const COMMENT_TEMPLATES = {
  menunggu: [
    {
      label: 'Konfirmasi Diterima',
      text: `Yth. Pelapor,

Kami ingin memberitahukan bahwa laporan yang Anda sampaikan telah berhasil kami terima dan tercatat dalam sistem Whistleblowing System (WBS) Inspektorat. Laporan Anda saat ini sedang memasuki tahap verifikasi awal untuk memastikan kelengkapan dan keabsahan informasi yang diberikan.

Tim kami akan segera meninjau isi laporan dan memverifikasi identitas serta bukti-bukti yang telah Anda lampirkan. Selama proses ini berlangsung, status laporan Anda akan tetap tercatat sebagai "Menunggu" hingga verifikasi selesai dilakukan.

Anda dapat memantau perkembangan laporan kapan saja melalui kode pelacakan yang telah diberikan. Apabila terdapat informasi tambahan yang perlu kami tanyakan, kami akan menghubungi Anda melalui saluran komunikasi ini.

Terima kasih atas kepercayaan dan keberanian Anda dalam menyampaikan laporan ini. Kontribusi Anda sangat berarti bagi upaya penegakan integritas dan tata kelola yang baik di lingkungan kami.

Hormat kami,
Tim Inspektorat`,
    },
    {
      label: 'Minta Kelengkapan',
      text: `Yth. Pelapor,

Terima kasih telah mempercayakan laporan Anda kepada kami. Setelah melakukan kajian awal terhadap laporan yang masuk, kami menemukan bahwa beberapa informasi pendukung masih perlu dilengkapi agar proses verifikasi dapat berjalan secara optimal.

Untuk mempercepat proses penanganan laporan Anda, kami memohon kesediaan Anda untuk melengkapi hal-hal berikut:

1. Kronologi kejadian yang lebih rinci dan berurutan sesuai tanggal/waktu
2. Nama lengkap dan jabatan pihak yang dilaporkan (bila diketahui)
3. Lokasi kejadian secara spesifik (nama unit kerja, gedung, atau alamat)
4. Dokumen pendukung seperti foto, surat, tangkapan layar, atau bukti transaksi yang relevan
5. Keterangan saksi atau pihak lain yang mengetahui kejadian tersebut (opsional)

Semakin lengkap informasi yang Anda berikan, semakin cepat dan akurat proses penanganan laporan ini dapat dilakukan. Mohon sampaikan informasi tambahan tersebut melalui kolom komunikasi ini.

Hormat kami,
Tim Inspektorat`,
    },
    {
      label: 'Estimasi Waktu',
      text: `Yth. Pelapor,

Laporan yang Anda sampaikan telah kami terima dan saat ini sedang dalam antrian proses verifikasi oleh tim Inspektorat. Kami ingin memberikan gambaran mengenai tahapan dan estimasi waktu penanganan laporan Anda.

Tahapan proses yang akan dilalui:
• Verifikasi awal dan kelengkapan berkas : 1–2 hari kerja
• Kajian substansi dan penilaian risiko : 2–3 hari kerja
• Koordinasi dan investigasi lapangan : 5–10 hari kerja
• Penyusunan hasil dan tindak lanjut : 3–5 hari kerja

Estimasi total waktu penyelesaian berkisar antara 10–20 hari kerja terhitung sejak laporan dinyatakan lengkap dan valid. Estimasi ini dapat berubah bergantung pada kompleksitas kasus dan ketersediaan bukti yang mendukung.

Kami memohon kesabaran Anda selama proses berlangsung. Anda akan mendapatkan notifikasi dan pembaruan status secara berkala. Jika ada pertanyaan, jangan ragu untuk menyampaikannya melalui saluran komunikasi ini.

Hormat kami,
Tim Inspektorat`,
    },
  ],
  diproses: [
    {
      label: 'Update Proses',
      text: `Yth. Pelapor,

Kami ingin menyampaikan pembaruan mengenai perkembangan penanganan laporan Anda. Saat ini laporan yang Anda sampaikan telah memasuki tahap investigasi aktif oleh tim Inspektorat.

Pada tahap ini, tim kami sedang melakukan serangkaian kegiatan sebagai berikut:
• Analisis mendalam terhadap seluruh informasi dan bukti yang telah dilampirkan
• Pemeriksaan dokumen-dokumen yang berkaitan dengan dugaan pelanggaran
• Konfirmasi silang data dengan sumber informasi internal yang relevan
• Penyusunan kronologi dan pemetaan dugaan pelanggaran secara sistematis

Proses investigasi ini memerlukan ketelitian dan kecermatan tinggi agar hasil yang diperoleh benar-benar akurat, objektif, dan dapat dipertanggungjawabkan. Kami berkomitmen untuk menyelesaikan proses ini dengan penuh integritas dan profesionalisme.

Apabila selama proses investigasi kami memerlukan informasi atau klarifikasi tambahan dari Anda, kami akan segera menghubungi Anda melalui saluran komunikasi ini. Mohon tetap memantau status laporan Anda secara berkala.

Hormat kami,
Tim Inspektorat`,
    },
    {
      label: 'Minta Klarifikasi',
      text: `Yth. Pelapor,

Dalam rangka kelancaran proses investigasi laporan Anda, kami membutuhkan klarifikasi dan informasi tambahan yang lebih spesifik terkait beberapa hal yang belum terjawab secara lengkap dalam laporan awal.

Mohon kiranya Anda dapat memberikan penjelasan mengenai hal-hal berikut:

1. Kronologi kejadian: Mohon diuraikan secara berurutan mulai dari awal mula kejadian, tahapan-tahapan yang berlangsung, hingga kondisi terakhir yang Anda ketahui.
2. Frekuensi kejadian: Apakah dugaan pelanggaran ini terjadi satu kali atau berulang kali? Jika berulang, mohon sebutkan periode waktunya.
3. Pihak yang terlibat: Selain pihak yang telah disebutkan, apakah ada pihak lain yang Anda ketahui turut terlibat atau mengetahui kejadian ini?
4. Kerugian yang ditimbulkan: Apakah Anda dapat mengestimasi besaran kerugian (materil/imateril) yang ditimbulkan akibat pelanggaran tersebut?

Informasi yang Anda berikan akan sangat membantu mempercepat dan mempertajam proses investigasi. Seluruh keterangan yang Anda sampaikan dijamin kerahasiaannya.

Hormat kami,
Tim Inspektorat`,
    },
    {
      label: 'Koordinasi Pihak Terkait',
      text: `Yth. Pelapor,

Kami ingin memberitahukan bahwa laporan Anda saat ini sedang dalam proses koordinasi lintas unit. Mengingat substansi laporan yang memerlukan keterlibatan beberapa pihak, Tim Inspektorat telah mengambil langkah-langkah koordinasi sebagai berikut:

• Penyampaian hasil kajian awal kepada pimpinan unit terkait
• Koordinasi dengan pejabat pengawas internal untuk pendalaman kasus
• Permintaan data dan dokumen pendukung dari unit kerja yang bersangkutan
• Penjadwalan pemeriksaan langsung terhadap pihak-pihak yang relevan

Proses koordinasi ini dirancang untuk memastikan bahwa penanganan laporan Anda dilakukan secara komprehensif, transparan, dan sesuai dengan mekanisme pengawasan internal yang berlaku.

Kami memperkirakan proses koordinasi ini akan membutuhkan waktu beberapa hari kerja ke depan. Anda akan mendapatkan pembaruan kembali setelah koordinasi selesai dan hasilnya dapat kami sampaikan. Mohon bersabar dan tetap memantau perkembangan laporan Anda.

Hormat kami,
Tim Inspektorat`,
    },
  ],
  selesai: [
    {
      label: 'Selesai - Ditindaklanjuti',
      text: `Yth. Pelapor,

Dengan hormat, kami ingin memberitahukan bahwa proses penanganan laporan yang Anda sampaikan telah dinyatakan selesai. Seluruh tahapan investigasi, mulai dari verifikasi awal, analisis bukti, pemeriksaan pihak terkait, hingga penyusunan rekomendasi, telah dilaksanakan sesuai dengan prosedur dan ketentuan yang berlaku.

Berdasarkan hasil investigasi yang telah dilakukan, laporan Anda terbukti memiliki dasar yang cukup dan telah ditindaklanjuti dengan langkah-langkah berikut:
• Pemberian rekomendasi tindakan korektif kepada pimpinan unit terkait
• Penjatuhan sanksi administratif sesuai peraturan yang berlaku
• Perbaikan sistem dan prosedur untuk mencegah terulangnya kejadian serupa

Kami memastikan bahwa tindak lanjut dilaksanakan secara konsisten dan terukur. Kerahasiaan identitas Anda sebagai pelapor tetap terjaga sepenuhnya sepanjang proses ini.

Atas keberanian dan kepedulian Anda dalam menyampaikan laporan ini, kami menyampaikan apresiasi yang setinggi-tingginya. Kontribusi nyata seperti ini adalah fondasi dari terwujudnya tata kelola pemerintahan yang bersih dan berintegritas.

Hormat kami,
Tim Inspektorat`,
    },
    {
      label: 'Selesai - Tidak Terbukti',
      text: `Yth. Pelapor,

Dengan hormat, kami ingin menyampaikan hasil akhir dari proses investigasi atas laporan yang Anda ajukan. Setelah melalui serangkaian tahapan investigasi yang komprehensif, cermat, dan penuh kehati-hatian, Tim Inspektorat menyimpulkan bahwa dugaan pelanggaran yang dilaporkan tidak dapat dibuktikan kebenarannya berdasarkan alat bukti yang tersedia.

Adapun dasar kesimpulan tersebut adalah sebagai berikut:
• Bukti-bukti yang ada tidak secara langsung dan meyakinkan mendukung dugaan pelanggaran
• Keterangan dari pihak-pihak yang diperiksa tidak menguatkan substansi laporan
• Dokumen dan data yang ditelaah tidak menunjukkan adanya indikasi penyimpangan

Perlu kami tegaskan bahwa hasil ini bukan berarti laporan Anda diabaikan. Setiap laporan yang masuk ditangani dengan standar yang sama, dan kami menghargai niat baik Anda dalam menyampaikan informasi yang Anda anggap perlu dilaporkan.

Apabila di kemudian hari Anda memperoleh bukti baru atau informasi tambahan yang relevan, Anda dipersilakan untuk mengajukan laporan baru melalui sistem WBS. Kami akan menanganinya dengan penuh tanggung jawab.

Hormat kami,
Tim Inspektorat`,
    },
    {
      label: 'Apresiasi Pelapor',
      text: `Yth. Pelapor,

Proses penanganan laporan Anda telah resmi dinyatakan selesai. Atas nama Inspektorat, kami ingin menyampaikan penghargaan dan apresiasi yang sebesar-besarnya atas keberanian, kepedulian, dan tanggung jawab sosial yang telah Anda tunjukkan.

Melaporkan dugaan pelanggaran bukanlah hal yang mudah. Dibutuhkan keberanian luar biasa untuk berdiri di atas kebenaran, dan itulah yang telah Anda lakukan. Tindakan Anda mencerminkan komitmen nyata terhadap nilai-nilai integritas, transparansi, dan akuntabilitas yang menjadi pilar utama organisasi yang sehat.

Laporan yang Anda sampaikan telah memberikan kontribusi yang sangat signifikan dalam:
• Mengidentifikasi potensi kelemahan dalam sistem pengawasan internal
• Mendorong perbaikan tata kelola di lingkungan yang bersangkutan
• Menguatkan budaya antikorupsi di seluruh jajaran organisasi

Kami berharap pengalaman ini tidak menyurutkan semangat Anda untuk terus berkontribusi. Jika di masa mendatang Anda kembali menyaksikan atau mengetahui adanya indikasi pelanggaran, jangan ragu untuk menggunakan saluran WBS ini kembali.

Bersama-sama, kita jaga integritas untuk Indonesia yang lebih baik.

Hormat kami,
Tim Inspektorat`,
    },
  ],
  ditolak: [
    {
      label: 'Bukti Tidak Cukup',
      text: `Yth. Pelapor,

Setelah melakukan kajian dan penilaian secara seksama terhadap laporan yang Anda sampaikan, dengan menyesal kami harus memberitahukan bahwa laporan ini tidak dapat diproses lebih lanjut pada tahap ini. Alasan utamanya adalah karena bukti dan informasi pendukung yang dilampirkan belum memadai untuk memulai proses investigasi secara formal.

Berikut adalah kekurangan yang menjadi dasar keputusan ini:
• Bukti dokumenter (foto, dokumen, tangkapan layar) yang dilampirkan belum cukup konkret
• Kronologi kejadian yang disampaikan masih bersifat umum dan kurang terperinci
• Identitas atau jabatan pihak yang dilaporkan belum dapat dikonfirmasi
• Kaitan antara dugaan pelanggaran dengan bukti yang ada belum tergambar dengan jelas

Kami sangat menyarankan Anda untuk tidak berkecil hati. Apabila Anda dapat mengumpulkan bukti-bukti yang lebih konkret dan terverifikasi, Anda dipersilakan untuk mengajukan laporan baru melalui sistem WBS ini. Semakin kuat bukti yang Anda sertakan, semakin besar kemungkinan laporan dapat segera ditindaklanjuti.

Jika Anda membutuhkan panduan mengenai jenis bukti apa saja yang dapat diterima, jangan ragu untuk bertanya melalui saluran komunikasi ini.

Hormat kami,
Tim Inspektorat`,
    },
    {
      label: 'Di Luar Kewenangan',
      text: `Yth. Pelapor,

Terima kasih telah menyampaikan laporan Anda melalui sistem WBS Inspektorat. Setelah dilakukan penelaahan terhadap substansi laporan, kami menyampaikan bahwa penanganan atas dugaan pelanggaran yang Anda laporkan berada di luar lingkup kewenangan dan yurisdiksi unit Inspektorat kami.

Hal ini dikarenakan:
• Dugaan pelanggaran yang dilaporkan berkaitan dengan pihak atau instansi di luar jangkauan pengawasan unit kami
• Jenis pelanggaran yang dilaporkan termasuk dalam ranah hukum pidana/perdata yang memerlukan penanganan oleh aparat penegak hukum
• Substansi laporan berkaitan dengan kebijakan yang berada di kewenangan instansi lain

Untuk memastikan laporan Anda mendapatkan penanganan yang tepat, kami menyarankan Anda untuk meneruskan laporan ini kepada instansi yang berwenang, antara lain:
• Komisi Pemberantasan Korupsi (KPK) — kpk.go.id / hotline 198
• Ombudsman Republik Indonesia — ombudsman.go.id
• Aparat Pengawasan Intern Pemerintah (APIP) instansi terkait
• Kepolisian Republik Indonesia (jika berkaitan dengan tindak pidana)

Kami berharap rekomendasi ini dapat membantu Anda mendapatkan tindak lanjut yang semestinya.

Hormat kami,
Tim Inspektorat`,
    },
    {
      label: 'Duplikasi Laporan',
      text: `Yth. Pelapor,

Terima kasih atas kepedulian Anda dalam menyampaikan laporan melalui sistem WBS Inspektorat. Setelah dilakukan verifikasi dan pencocokan data di sistem kami, laporan yang baru saja Anda ajukan teridentifikasi memiliki substansi yang sama atau sangat serupa dengan laporan yang sebelumnya telah lebih dulu terdaftar dalam sistem kami.

Untuk menjaga efisiensi proses dan menghindari duplikasi sumber daya investigasi, laporan ini tidak akan diproses secara terpisah. Penanganan akan dilakukan secara terpadu melalui laporan yang pertama kali masuk.

Yang dapat Anda lakukan selanjutnya:
• Pantau status perkembangan laporan Anda yang pertama menggunakan kode pelacakan yang telah diberikan sebelumnya
• Apabila Anda memiliki informasi atau bukti baru yang belum disertakan sebelumnya, silakan sampaikan melalui kolom komunikasi di laporan pertama
• Jika Anda belum memiliki kode pelacakan laporan pertama, mohon hubungi kami untuk klarifikasi lebih lanjut

Kami tetap berkomitmen untuk menangani setiap laporan dengan serius dan profesional. Seluruh informasi yang Anda berikan akan menjadi bahan pertimbangan yang berharga dalam proses investigasi.

Hormat kami,
Tim Inspektorat`,
    },
  ],
}

const CATEGORY_EMOJI = {
  'Korupsi & Gratifikasi': '🏛️', 'Penyalahgunaan Wewenang': '⚖️',
  'Konflik Kepentingan': '🔄', 'Penggelapan Aset Negara': '📦',
  'Suap & Pungli': '💰', 'Pemalsuan Dokumen': '📄',
  'Pelanggaran Kode Etik': '📋', 'Kecurangan Pengadaan': '🛒',
}

const STATUS_LABELS = {
  menunggu: 'Menunggu',
  diproses: 'Sedang Diproses',
  selesai:  'Selesai',
  ditolak:  'Ditolak',
}

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function isPreviewable(contentType) {
  if (!contentType) return false
  return (
    contentType.startsWith('image/') ||
    contentType === 'application/pdf' ||
    contentType.startsWith('video/')
  )
}

function fileIcon(contentType) {
  if (!contentType) return faFileAlt
  if (contentType.startsWith('image/')) return faFileImage
  if (contentType === 'application/pdf') return faFilePdf
  if (contentType.startsWith('video/')) return faFileVideo
  return faFileAlt
}

function PKSModal({ agreedAt, reportId, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
          <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faShieldHalved} className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-gray-900 text-sm">PKS Kerahasiaan Pelapor</h2>
            <p className="text-[10px] text-gray-400 mt-0.5">Perjanjian Kerahasiaan dan Perlindungan Identitas Pelapor</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">
          <pre className="text-[11.5px] text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">{AGREEMENT_TEXT}</pre>
        </div>
        <div className="px-6 py-3.5 bg-emerald-50 border-t border-emerald-100 flex items-center justify-between gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FontAwesomeIcon icon={faShieldHalved} className="w-3 h-3 text-emerald-500 flex-shrink-0" />
            <p className="text-[11px] text-emerald-700 font-medium truncate">Disetujui dan ditandatangani secara digital pada {agreedAt}</p>
          </div>
          {reportId && (
            <a
              href={`/admin/reports/${reportId}/pks_pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold rounded-lg transition-colors flex-shrink-0"
            >
              <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
              Unduh PDF
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function PreviewModal({ file, onClose }) {
  const handleBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget) onClose()
  }, [onClose])

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const previewUrl = file.inline_url

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={fileIcon(file.content_type)} className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{file.name}</p>
              <p className="text-[11px] text-gray-400">{formatBytes(file.size)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <a
              href={file.url}
              download
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
              Unduh
            </a>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            >
              <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Preview body */}
        <div className="flex-1 overflow-auto bg-gray-50 min-h-0">
          {file.content_type?.startsWith('image/') ? (
            <div className="flex items-center justify-center w-full h-full p-4">
              <img
                src={previewUrl}
                alt={file.name}
                className="max-w-full max-h-full object-contain rounded-lg shadow"
              />
            </div>
          ) : file.content_type === 'application/pdf' ? (
            <iframe
              src={previewUrl}
              title={file.name}
              className="w-full h-full min-h-[60vh]"
              style={{ border: 'none' }}
            />
          ) : file.content_type?.startsWith('video/') ? (
            <div className="flex items-center justify-center w-full h-full p-4">
              <video
                src={previewUrl}
                controls
                className="max-w-full max-h-full rounded-lg shadow"
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const ACTION_CONFIG = {
  submitted: {
    icon: faEnvelopeOpen,
    bg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    lineBg: 'bg-emerald-200',
    label: 'Laporan Diterima',
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-700',
    badgeRing: 'ring-emerald-200',
  },
  status_changed: {
    icon: faArrowRightArrowLeft,
    bg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    lineBg: 'bg-amber-200',
    label: 'Status Diperbarui',
    badgeBg: 'bg-amber-50',
    badgeText: 'text-amber-700',
    badgeRing: 'ring-amber-200',
  },
  identity_requested: {
    icon: faIdCard,
    bg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    lineBg: 'bg-amber-200',
    label: 'Admin Minta Identitas',
    badgeBg: 'bg-amber-50',
    badgeText: 'text-amber-700',
    badgeRing: 'ring-amber-200',
  },
  identity_cancelled: {
    icon: faXmark,
    bg: 'bg-gray-100',
    iconColor: 'text-gray-500',
    lineBg: 'bg-gray-200',
    label: 'Permintaan Dibatalkan',
    badgeBg: 'bg-gray-50',
    badgeText: 'text-gray-600',
    badgeRing: 'ring-gray-200',
  },
  identity_approved: {
    icon: faUserCheck,
    bg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    lineBg: 'bg-emerald-200',
    label: 'Identitas Dibuka',
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-700',
    badgeRing: 'ring-emerald-200',
  },
  identity_rejected: {
    icon: faCircleXmark,
    bg: 'bg-red-100',
    iconColor: 'text-red-500',
    lineBg: 'bg-red-200',
    label: 'Identitas Ditolak',
    badgeBg: 'bg-red-50',
    badgeText: 'text-red-600',
    badgeRing: 'ring-red-200',
  },
  communication_opened: {
    icon: faLockOpen,
    bg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    lineBg: 'bg-emerald-200',
    label: 'Komunikasi Dibuka',
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-700',
    badgeRing: 'ring-emerald-200',
  },
  communication_closed: {
    icon: faLock,
    bg: 'bg-gray-100',
    iconColor: 'text-gray-500',
    lineBg: 'bg-gray-200',
    label: 'Komunikasi Ditutup',
    badgeBg: 'bg-gray-50',
    badgeText: 'text-gray-600',
    badgeRing: 'ring-gray-200',
  },
  file_request_opened: {
    icon: faFileArrowUp,
    bg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    lineBg: 'bg-violet-200',
    label: 'Permintaan File Bukti',
    badgeBg: 'bg-violet-50',
    badgeText: 'text-violet-700',
    badgeRing: 'ring-violet-200',
  },
  file_request_closed: {
    icon: faFileCircleCheck,
    bg: 'bg-slate-100',
    iconColor: 'text-slate-500',
    lineBg: 'bg-slate-200',
    label: 'Permintaan File Selesai',
    badgeBg: 'bg-slate-50',
    badgeText: 'text-slate-600',
    badgeRing: 'ring-slate-200',
  },
  admin_message_sent: {
    icon: faComments,
    bg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    lineBg: 'bg-blue-200',
    label: 'Admin Mengirim Pesan',
    badgeBg: 'bg-blue-50',
    badgeText: 'text-blue-700',
    badgeRing: 'ring-blue-200',
  },
  user_message_sent: {
    icon: faComments,
    bg: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
    lineBg: 'bg-cyan-200',
    label: 'Pelapor Mengirim Pesan',
    badgeBg: 'bg-cyan-50',
    badgeText: 'text-cyan-700',
    badgeRing: 'ring-cyan-200',
  },
  user_file_uploaded: {
    icon: faFileCircleCheck,
    bg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    lineBg: 'bg-violet-200',
    label: 'Pelapor Kirim File Bukti',
    badgeBg: 'bg-violet-50',
    badgeText: 'text-violet-700',
    badgeRing: 'ring-violet-200',
  },
}

const STATUS_LABEL_MAP = {
  menunggu: 'Menunggu',
  diproses: 'Sedang Diproses',
  selesai: 'Selesai',
  ditolak: 'Ditolak',
}

const STATUS_PILL = {
  menunggu: 'bg-gray-100 text-gray-600',
  diproses: 'bg-amber-50 text-amber-700',
  selesai: 'bg-emerald-50 text-emerald-700',
  ditolak: 'bg-red-50 text-red-600',
}

function ActivityTimeline({ activities }) {
  if (!activities?.length) return null
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
        <div className="w-7 h-7 bg-slate-100 rounded-lg flex items-center justify-center">
          <FontAwesomeIcon icon={faClockRotateLeft} className="w-3.5 h-3.5 text-slate-500" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900 text-sm">Riwayat Aktivitas</h2>
          <p className="text-[10px] text-gray-400">{activities.length} kejadian tercatat</p>
        </div>
      </div>

      <div className="p-5">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-4 top-5 bottom-5 w-0.5 bg-gradient-to-b from-gray-200 via-gray-200 to-transparent" />

          <div className="space-y-0">
            {activities.map((act, idx) => {
              const cfg = ACTION_CONFIG[act.action_type] || ACTION_CONFIG.status_changed
              const isLast = idx === activities.length - 1
              return (
                <div key={act.id} className={`relative flex gap-4 ${isLast ? '' : 'pb-6'}`}>
                  {/* Icon dot */}
                  <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${cfg.bg}`}>
                    <FontAwesomeIcon icon={cfg.icon} className={`w-3.5 h-3.5 ${cfg.iconColor}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ${cfg.badgeBg} ${cfg.badgeText} ${cfg.badgeRing}`}>
                          {cfg.label}
                        </span>
                        {act.action_type !== 'submitted' && act.admin_name && (
                          <span className="text-[11px] text-gray-400 flex items-center gap-1">
                            <FontAwesomeIcon icon={faUser} className="w-2.5 h-2.5" />
                            {act.admin_name}
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">{act.created_at}</span>
                    </div>

                    {/* Status change pill */}
                    {act.action_type !== 'submitted' && (act.old_status || act.new_status) && (
                      <div className="flex items-center gap-1.5 mb-2">
                        {act.old_status && (
                          <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_PILL[act.old_status] || 'bg-gray-100 text-gray-500'}`}>
                            {STATUS_LABEL_MAP[act.old_status] || act.old_status}
                          </span>
                        )}
                        {act.old_status && act.new_status && act.old_status !== act.new_status && (
                          <FontAwesomeIcon icon={faArrowRightArrowLeft} className="w-2.5 h-2.5 text-gray-300" />
                        )}
                        {act.new_status && act.new_status !== act.old_status && (
                          <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 ${STATUS_PILL[act.new_status] || 'bg-gray-100 text-gray-500'} ring-current/20`}>
                            {STATUS_LABEL_MAP[act.new_status] || act.new_status}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Submission note */}
                    {act.action_type === 'submitted' && (
                      <p className="text-xs text-gray-500">Laporan berhasil masuk ke sistem WBS dan menunggu verifikasi tim Inspektorat.</p>
                    )}

                    {/* Feedback note */}
                    {act.note && act.action_type !== 'submitted' && (
                      <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5 mt-1">
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">
                          {act.action_type.startsWith('identity_') ? 'Catatan' : 'Tanggapan'}
                        </p>
                        <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line line-clamp-4">{act.note}</p>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function AdminReportsShow({ current_user, report, admins = [] }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const assignForm = useForm({ assigned_admin_id: report.assigned_admin?.id?.toString() || '' })
  const [showPKS, setShowPKS] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [comments, setComments] = useState(report.comments || [])
  const [communicationOpen, setCommunicationOpen] = useState(report.communication_open ?? false)
  const [fileRequestOpen, setFileRequestOpen] = useState(report.file_request_open ?? false)
  const [evidenceFilesCount, setEvidenceFilesCount] = useState(report.evidence_files_count ?? 0)
  const [identityRequested, setIdentityRequested] = useState(report.identity_reveal_requested ?? false)
  const [identityApproved, setIdentityApproved] = useState(report.identity_reveal_approved ?? false)
  const [identityAgreedAt, setIdentityAgreedAt] = useState(report.identity_reveal_agreed_at ?? null)
  const [identityReporter, setIdentityReporter] = useState(report.identity_reporter ?? null)
  const [activities, setActivities] = useState(report.activities || [])
  const [evidenceFiles, setEvidenceFiles] = useState(
    (report.comments || []).filter(c => c.role === 'user' && c.attachment).map(c => ({
      ...c.attachment,
      sent_at: c.created_at,
      sender:  c.user_name,
    }))
  )
  const [commentBody, setCommentBody] = useState('')
  const [sendingComment, setSendingComment] = useState(false)
  const commentsEndRef = useRef(null)
  const cableRef = useRef(null)
  const textareaRef = useRef(null)

  useEffect(() => {
    const consumer = createConsumer('/cable')
    cableRef.current = consumer.subscriptions.create(
      { channel: 'ReportCommentsChannel', report_id: report.id },
      {
        received(data) {
          if (data.type === 'communication_toggle') {
            setCommunicationOpen(data.communication_open)
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.type === 'file_request_toggle') {
            setFileRequestOpen(data.file_request_open)
            setEvidenceFilesCount(data.evidence_files_count)
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.type === 'identity_reveal_approved') {
            setIdentityApproved(true)
            setIdentityAgreedAt(data.identity_reveal_agreed_at)
            setIdentityReporter(data.identity_reporter)
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.type === 'identity_reveal_rejected') {
            setIdentityRequested(false)
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.type === 'identity_reveal_cancelled') {
            setIdentityRequested(false)
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.type === 'identity_reveal_request') {
            setIdentityRequested(true)
            if (data.activity) setActivities(prev => [...prev, data.activity])
            return
          }
          if (data.evidence_files_count !== undefined) {
            setEvidenceFilesCount(data.evidence_files_count)
            setFileRequestOpen(data.file_request_open)
          }
          if (data.activity) setActivities(prev => [...prev, data.activity])
          if (data.role === 'user' && data.attachment) {
            setEvidenceFiles(prev => [...prev, {
              ...data.attachment,
              sent_at: data.created_at,
              sender:  data.user_name,
            }])
          }
          setComments(prev => {
            if (prev.some(c => c.id === data.id)) return prev
            return [...prev, data]
          })
        }
      }
    )
    return () => {
      cableRef.current?.unsubscribe()
      consumer.disconnect()
    }
  }, [report.id])

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [comments])

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }, [commentBody])

  const sendComment = async (e) => {
    e.preventDefault()
    const body = commentBody.trim()
    if (!body || sendingComment) return
    setSendingComment(true)
    try {
      const res = await fetch(`/admin/reports/${report.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ body }),
      })
      if (res.ok) setCommentBody('')
    } finally {
      setSendingComment(false)
    }
  }

  const toggleCommunication = async () => {
    const res = await fetch(`/admin/reports/${report.id}/toggle_communication`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      },
    })
    if (res.ok) {
      const data = await res.json()
      setCommunicationOpen(data.communication_open)
    }
  }

  const requestEvidence = async () => {
    const res = await fetch(`/admin/reports/${report.id}/request_evidence`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      },
    })
    if (res.ok) {
      const data = await res.json()
      setFileRequestOpen(data.file_request_open)
      setEvidenceFilesCount(data.evidence_files_count)
    }
  }

  const requestIdentity = async () => {
    const res = await fetch(`/admin/reports/${report.id}/request_identity`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
    })
    if (res.ok) setIdentityRequested(true)
  }

  const cancelIdentity = async () => {
    const res = await fetch(`/admin/reports/${report.id}/cancel_identity`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
    })
    if (res.ok) setIdentityRequested(false)
  }

  const form = useForm({
    status: report.status,
  })

  const cfg = STATUS_CONFIG[report.status] || STATUS_CONFIG.menunggu

  const handleSubmit = (e) => {
    e.preventDefault()
    form.patch(`/admin/reports/${report.id}`)
  }

  const STATUS_BUTTON_COLORS = {
    menunggu: 'bg-gray-100 text-gray-700 hover:bg-gray-200 ring-1 ring-gray-300',
    diproses: 'bg-amber-50 text-amber-700 hover:bg-amber-100 ring-1 ring-amber-300',
    selesai:  'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ring-1 ring-emerald-300',
    ditolak:  'bg-red-50 text-red-600 hover:bg-red-100 ring-1 ring-red-300',
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <AdminSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <AdminTopbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">

          {/* Back + heading */}
          <div className="flex items-center gap-3 mb-6">
            <a href="/admin/reports"
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-500 transition-colors">
              <FontAwesomeIcon icon={faChevronLeft} className="w-3.5 h-3.5" />
            </a>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Detail Laporan</h1>
              <p className="text-sm text-gray-500 mt-0.5">Tinjau laporan dan berikan tanggapan resmi</p>
            </div>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-7 gap-5">

            {/* === LEFT COLUMN (2/3) === */}
            <div className="lg:col-span-4 space-y-5">

              {/* Tracking + status card */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className={`h-1.5 ${cfg.bar}`} />
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Kode Pelacakan</p>
                      <span className="text-lg font-bold font-mono text-gray-900 tracking-wider">{report.tracking_code}</span>
                      <p className="text-xs text-gray-400 mt-1">Dilaporkan pada {report.created_at}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ring-1 ${cfg.bg} ${cfg.text} ${cfg.ring}`}>
                      <FontAwesomeIcon icon={cfg.icon} className="w-3.5 h-3.5" />
                      {cfg.label}
                    </span>
                  </div>
                  {/* Progress */}
                  <div className="mt-4">
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${cfg.bar} rounded-full transition-all duration-700`} style={{ width: `${cfg.pct}%` }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 mt-1.5">
                      <span>Diterima</span><span>Verifikasi</span><span>Diproses</span><span>Selesai</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Report detail */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <span className="text-lg">{CATEGORY_EMOJI[report.category] || '📋'}</span>
                  <h2 className="font-semibold text-gray-900 text-sm">{report.category}</h2>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Judul Laporan</p>
                    <p className="text-gray-900 font-semibold text-sm">{report.title}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {report.location && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Lokasi</p>
                        <p className="text-gray-700 text-sm flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faLocationDot} className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          {report.location}
                        </p>
                      </div>
                    )}
                    {report.incident_date && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Tgl. Kejadian</p>
                        <p className="text-gray-700 text-sm flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faCalendar} className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          {report.incident_date}
                        </p>
                      </div>
                    )}
                  </div>

                  {report.anonymous && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                      <FontAwesomeIcon icon={faEyeSlash} className="w-3.5 h-3.5" />
                      Laporan ini dikirim secara anonim
                    </div>
                  )}

                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Uraian Pelanggaran</p>
                    <div
                      className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: report.description }}
                    />
                  </div>
                </div>
              </div>

              {/* Attachments */}
              {report.attachments?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faPaperclip} className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                      <h2 className="font-semibold text-gray-900 text-sm">Berkas Bukti</h2>
                    </div>
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      {report.attachments.length}
                    </span>
                  </div>
                  <div className="p-3 space-y-1.5">
                    {report.attachments.map((att, i) => {
                      const canPreview = isPreviewable(att.content_type)
                      const icon = fileIcon(att.content_type)
                      const isImage = att.content_type?.startsWith('image/')
                      const isPdf   = att.content_type === 'application/pdf'
                      const iconColor = isImage ? 'text-emerald-500' : isPdf ? 'text-red-500' : 'text-blue-500'
                      const iconBg    = isImage ? 'bg-emerald-50'    : isPdf ? 'bg-red-50'    : 'bg-blue-50'
                      const inner = (
                        <>
                          <div className={`w-8 h-8 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <FontAwesomeIcon icon={icon} className={`w-3.5 h-3.5 ${iconColor}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">{att.name}</p>
                            <p className="text-[10px] text-gray-400">{formatBytes(att.size)}</p>
                          </div>
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-white transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100 group-hover:bg-blue-600">
                            <FontAwesomeIcon icon={canPreview ? faExpand : faDownload} className="w-3 h-3" />
                          </div>
                        </>
                      )
                      return canPreview ? (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setPreviewFile(att)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition-all group text-left"
                        >
                          {inner}
                        </button>
                      ) : (
                        <a
                          key={i}
                          href={att.url}
                          download
                          className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition-all group"
                        >
                          {inner}
                        </a>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Comments / Komunikasi */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faComments} className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-semibold text-gray-900 text-sm">Komunikasi</h2>
                    <p className="text-[11px] text-gray-400">Pesan antara admin dan pelapor</p>
                  </div>
                  <button
                    onClick={toggleCommunication}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      communicationOpen
                        ? 'bg-red-50 text-red-600 hover:bg-red-100 ring-1 ring-red-200'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ring-1 ring-emerald-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={communicationOpen ? faLock : faLockOpen} className="w-3 h-3" />
                    {communicationOpen ? 'Tutup Komunikasi' : 'Buka Komunikasi'}
                  </button>
                  {communicationOpen && (
                    <button
                      onClick={requestEvidence}
                      title={fileRequestOpen ? `Tutup permintaan file (${evidenceFilesCount}/5 terkirim)` : 'Minta pelapor kirim file bukti'}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        fileRequestOpen
                          ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 ring-1 ring-amber-300'
                          : 'bg-violet-50 text-violet-700 hover:bg-violet-100 ring-1 ring-violet-200'
                      }`}
                    >
                      <FontAwesomeIcon icon={fileRequestOpen ? faFileCircleCheck : faFileArrowUp} className="w-3 h-3" />
                      {fileRequestOpen ? `File Diminta (${evidenceFilesCount}/5)` : 'Minta File Bukti'}
                    </button>
                  )}
                </div>

                {/* Message list */}
                <div className="p-4 space-y-3">
                  {comments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                      <FontAwesomeIcon icon={faComments} className="w-8 h-8 mb-2 opacity-30" />
                      <p className="text-sm">Belum ada pesan</p>
                    </div>
                  ) : (
                    comments.map(c => {
                      const isAdmin = c.role === 'admin'
                      return (
                        <div key={c.id} className={`flex gap-2.5 ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                          {!isAdmin && (
                            <div className="w-7 h-7 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <FontAwesomeIcon icon={faUser} className="w-3 h-3 text-cyan-600" />
                            </div>
                          )}
                          <div className={`max-w-[75%] ${isAdmin ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                            <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                              isAdmin
                                ? 'bg-blue-600 text-white rounded-tr-sm'
                                : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                            }`}>
                              {c.body}
                            </div>
                            {c.attachment && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (isPreviewable(c.attachment.content_type)) {
                                    setPreviewFile(c.attachment)
                                  } else {
                                    const a = document.createElement('a')
                                    a.href = c.attachment.url
                                    a.download = c.attachment.name
                                    a.click()
                                  }
                                }}
                                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-colors ${
                                  isAdmin
                                    ? 'bg-blue-500 border-blue-400 text-white hover:bg-blue-400'
                                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <FontAwesomeIcon icon={fileIcon(c.attachment.content_type)} className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="truncate max-w-[180px]">{c.attachment.name}</span>
                                <span className="opacity-60 flex-shrink-0">{formatBytes(c.attachment.size)}</span>
                                <FontAwesomeIcon icon={isPreviewable(c.attachment.content_type) ? faExpand : faDownload} className="w-3 h-3 flex-shrink-0" />
                              </button>
                            )}
                            <p className="text-[10px] text-gray-400 px-1">
                              {c.user_name} · {c.created_at}
                            </p>
                          </div>
                          {isAdmin && (
                            <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <FontAwesomeIcon icon={faUser} className="w-3 h-3 text-blue-600" />
                            </div>
                          )}
                        </div>
                      )
                    })
                  )}
                  <div ref={commentsEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={sendComment} className="px-4 pb-4">
                  {/* Template quick replies */}
                  {COMMENT_TEMPLATES[report.status]?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                        <FontAwesomeIcon icon={faBolt} className="w-2.5 h-2.5 text-amber-400" />
                        Template Balasan
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {COMMENT_TEMPLATES[report.status].map((tpl, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setCommentBody(tpl.text)}
                            className="px-2.5 py-1 text-[11px] font-medium rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                          >
                            {tpl.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 items-end">
                    <textarea
                      ref={textareaRef}
                      value={commentBody}
                      onChange={e => setCommentBody(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendComment(e) } }}
                      placeholder="Tulis pesan kepada pelapor..."
                      rows={1}
                      className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2.5 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none overflow-hidden"
                    />
                    <button
                      type="submit"
                      disabled={!commentBody.trim() || sendingComment}
                      className="w-10 h-10 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors disabled:opacity-40 flex-shrink-0"
                    >
                      <FontAwesomeIcon icon={faPaperPlane} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1.5">Enter untuk kirim · Shift+Enter baris baru</p>
                </form>
              </div>
            </div>

            {/* === RIGHT COLUMN === */}
            <div className="lg:col-span-3 space-y-4">

              {/* Penanggung jawab & SLA */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5">
                  <div className="w-7 h-7 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faUserCheck} className="w-3.5 h-3.5 text-violet-600" />
                  </div>
                  <h2 className="font-semibold text-gray-900 text-sm">Penanggung Jawab</h2>
                </div>
                <div className="p-4 space-y-3">
                  {report.due_at && (
                    <p className={`text-xs font-medium ${report.overdue ? 'text-red-600' : 'text-gray-500'}`}>
                      Jatuh tempo SLA: {report.due_at}{report.overdue ? ' (Terlambat)' : ''}
                    </p>
                  )}
                  <select
                    value={assignForm.data.assigned_admin_id}
                    onChange={e => assignForm.setData('assigned_admin_id', e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">Belum ditugaskan</option>
                    {admins.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={assignForm.processing}
                    onClick={() => assignForm.patch(`/admin/reports/${report.id}/assign`)}
                    className="w-full py-2 text-sm font-semibold bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-50"
                  >
                    {assignForm.processing ? 'Menyimpan...' : 'Simpan Penugasan'}
                  </button>
                </div>
              </div>

              {/* ── Informasi Pelapor ── */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5">
                  <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faUser} className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <h2 className="font-semibold text-gray-900 text-sm">Informasi Pelapor</h2>
                </div>
                <div className="p-4">
                  {report.anonymous ? (
                    identityApproved ? (
                      /* Identity approved – full revealed data */
                      <div className="space-y-4">
                        {/* Avatar row */}
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold ${identityReporter?.user_type === 'internal' ? 'bg-violet-100 text-violet-700' : 'bg-cyan-100 text-cyan-700'}`}>
                            {(identityReporter?.name || '?').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{identityReporter?.name || identityReporter?.username}</p>
                            <p className="text-xs text-gray-400 truncate">{identityReporter?.email}</p>
                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full mt-0.5 ${identityReporter?.user_type === 'internal' ? 'bg-violet-100 text-violet-600' : 'bg-cyan-100 text-cyan-600'}`}>
                              <FontAwesomeIcon icon={identityReporter?.user_type === 'internal' ? faBuilding : faGlobe} className="w-2.5 h-2.5" />
                              {identityReporter?.user_type}
                            </span>
                          </div>
                        </div>
                        {/* Detail fields */}
                        <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                          {[
                            { label: 'Telepon',   value: identityReporter?.phone },
                            { label: 'WhatsApp',  value: identityReporter?.whatsapp_number },
                            { label: 'NIK',       value: identityReporter?.nik, mono: true },
                            { label: 'Alamat',    value: identityReporter?.address },
                          ].filter(f => f.value).map(f => (
                            <div key={f.label} className="flex items-start gap-2">
                              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-16 flex-shrink-0 pt-0.5">{f.label}</span>
                              <span className={`text-xs text-gray-800 leading-relaxed ${f.mono ? 'font-mono' : 'font-medium'}`}>{f.value}</span>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => setShowPKS(true)}
                          className="w-full flex items-center gap-2.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl px-3 py-2.5 transition-colors text-left"
                        >
                          <FontAwesomeIcon icon={faShieldHalved} className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-[11px] font-semibold text-emerald-700">PKS Kerahasiaan Tersimpan</p>
                            <p className="text-[10px] text-emerald-600">Disetujui pada {identityAgreedAt}</p>
                          </div>
                          <FontAwesomeIcon icon={faEye} className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                        </button>
                      </div>
                    ) : identityRequested ? (
                      /* Waiting for approval */
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FontAwesomeIcon icon={faIdCard} className="w-3.5 h-3.5 text-amber-500" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-amber-700">Menunggu Persetujuan</p>
                            <p className="text-[11px] text-amber-500 flex items-center gap-1.5 mt-0.5">
                              <span className="inline-block w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
                              Permintaan terkirim ke pelapor
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={cancelIdentity}
                          className="w-full flex items-center justify-center gap-1.5 py-2 bg-white hover:bg-red-50 border border-gray-200 hover:border-red-200 text-gray-500 hover:text-red-600 text-xs font-semibold rounded-xl transition-colors"
                        >
                          <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
                          Batalkan Permintaan
                        </button>
                      </div>
                    ) : (
                      /* Not yet requested */
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-3.5 py-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FontAwesomeIcon icon={faEyeSlash} className="w-3.5 h-3.5 text-gray-400" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-600">Laporan Anonim</p>
                            <p className="text-[11px] text-gray-400">Identitas disembunyikan oleh pelapor</p>
                          </div>
                        </div>
                        <button
                          onClick={requestIdentity}
                          className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-xl transition-colors shadow-sm"
                        >
                          <FontAwesomeIcon icon={faIdCard} className="w-3.5 h-3.5" />
                          Minta Identitas Pelapor
                        </button>
                      </div>
                    )
                  ) : (
                    /* Known reporter */
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold ${report.reporter?.type === 'internal' ? 'bg-violet-100 text-violet-700' : 'bg-cyan-100 text-cyan-700'}`}>
                        {(report.reporter?.name || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{report.reporter?.name}</p>
                        <p className="text-xs text-gray-400 truncate">{report.reporter?.email}</p>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full mt-0.5 ${report.reporter?.type === 'internal' ? 'bg-violet-100 text-violet-600' : 'bg-cyan-100 text-cyan-600'}`}>
                          <FontAwesomeIcon icon={report.reporter?.type === 'internal' ? faBuilding : faGlobe} className="w-2.5 h-2.5" />
                          {report.reporter?.type}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Tindak Lanjut ── */}
              <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5">
                  <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faFlagCheckered} className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <h2 className="font-semibold text-gray-900 text-sm">Tindak Lanjut</h2>
                </div>
                <div className="p-4 space-y-4">

                  {/* Status selector */}
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Status Laporan</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(STATUS_LABELS).map(([key, label]) => {
                        const active = form.data.status === key
                        const scfg = STATUS_CONFIG[key]
                        return (
                          <button key={key} type="button"
                            onClick={() => form.setData('status', key)}
                            className={`flex items-center gap-1.5 justify-center py-2 px-2 rounded-xl text-xs font-semibold transition-all border ${
                              active
                                ? `${scfg.bg} ${scfg.text} border-transparent ring-2 ${scfg.ring} shadow-sm`
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 hover:text-gray-700'
                            }`}>
                            <FontAwesomeIcon icon={scfg.icon} className="w-3 h-3" />
                            {label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <button type="submit" disabled={form.processing}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 shadow-sm">
                    <FontAwesomeIcon icon={faPaperPlane} className="w-3.5 h-3.5" />
                    {form.processing ? 'Menyimpan...' : 'Simpan Status'}
                  </button>
                </div>
              </form>

              {/* ── File Bukti dari Pelapor ── */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faFileArrowUp} className="w-3.5 h-3.5 text-violet-600" />
                    </div>
                    <h2 className="font-semibold text-gray-900 text-sm">File Bukti Pelapor</h2>
                  </div>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${evidenceFiles.length > 0 ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-400'}`}>
                    {evidenceFiles.length}
                  </span>
                </div>
                <div className="p-3">
                  {evidenceFiles.length === 0 ? (
                    <div className="text-center py-7">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <FontAwesomeIcon icon={faFileArrowUp} className="w-4 h-4 text-gray-300" />
                      </div>
                      <p className="text-xs text-gray-400">Belum ada file yang dikirim</p>
                      {fileRequestOpen && (
                        <p className="text-[10px] text-violet-400 mt-1">Menunggu unggahan dari pelapor…</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {evidenceFiles.map((f, i) => {
                        const canPreview = isPreviewable(f.content_type)
                        const icon = fileIcon(f.content_type)
                        const isImage = f.content_type?.startsWith('image/')
                        const isPdf   = f.content_type === 'application/pdf'
                        const iconColor = isImage ? 'text-emerald-500' : isPdf ? 'text-red-500' : 'text-blue-500'
                        const iconBg    = isImage ? 'bg-emerald-50'    : isPdf ? 'bg-red-50'    : 'bg-blue-50'
                        const inner = (
                          <>
                            <div className={`w-8 h-8 ${iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                              <FontAwesomeIcon icon={icon} className={`w-3.5 h-3.5 ${iconColor}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">{f.name}</p>
                              <p className="text-[10px] text-gray-400">{formatBytes(f.size)}{f.size ? ' · ' : ''}{f.sent_at}</p>
                            </div>
                            <div className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-white transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100 group-hover:bg-violet-600">
                              <FontAwesomeIcon icon={canPreview ? faExpand : faDownload} className="w-3 h-3" />
                            </div>
                          </>
                        )
                        return canPreview ? (
                          <div
                            key={i}
                            onClick={() => setPreviewFile(f)}
                            className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 hover:border-violet-200 hover:bg-violet-50 transition-all group cursor-pointer"
                          >
                            {inner}
                          </div>
                        ) : (
                          <a
                            key={i}
                            href={f.url}
                            download
                            className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 hover:border-violet-200 hover:bg-violet-50 transition-all group"
                          >
                            {inner}
                          </a>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Activity Timeline ── */}
              <ActivityTimeline activities={activities} />

            </div>
          </div>
        </main>
      </div>

      {previewFile && (
        <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
      {showPKS && (
        <PKSModal agreedAt={identityAgreedAt} reportId={report.id} onClose={() => setShowPKS(false)} />
      )}
    </div>
  )
}
